"use client";

import { useEffect, useState, type ComponentPropsWithoutRef } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { BookCall } from "@/components/booking/BookCall";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/** In the order the sections appear on the page */
const LINKS = [
  { label: "Work", href: "#work" },
  { label: "Reviews", href: "#reviews" },
  { label: "How we work", href: "#about" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "#contact" },
];

const EASE = [0.22, 1, 0.36, 1] as const;

const NavLink = ({ href, ...rest }: { href: string } & ComponentPropsWithoutRef<"a">) =>
  href.startsWith("/") ? <Link href={href} {...rest} /> : <a href={href} {...rest} />;

/**
 * A sticky top bar that sits at the top of the page at all times.
 * Transparent over the hero, gains a solid white background with a
 * bottom border once the page scrolls past the fold.
 */
export const Navbar = ({ hasReviews = false, hasPosts = false }: { hasReviews?: boolean; hasPosts?: boolean }) => {
  // Reviews and Blog are filled from the CMS. Either one left empty loses its link: no link to nothing.
  const links = LINKS.filter(
    (link) => (link.href !== "#reviews" || hasReviews) && (link.href !== "/blog" || hasPosts),
  );
  const onHome = usePathname() === "/";
  const hrefFor = (href: string) => (onHome || !href.startsWith("#") ? href : `/${href}`);
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the menu on Escape and whenever the viewport grows past mobile.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    const mq = window.matchMedia("(min-width: 768px)");
    const onResize = () => mq.matches && setOpen(false);
    window.addEventListener("keydown", onKey);
    mq.addEventListener("change", onResize);
    return () => {
      window.removeEventListener("keydown", onKey);
      mq.removeEventListener("change", onResize);
    };
  }, [open]);

  return (
    <motion.header
      initial={{ y: -16, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: EASE, delay: 0.2 }}
      className={cn(
        "fixed inset-x-0 top-0 z-40 w-full transition-[background-color,border-color,box-shadow,backdrop-filter] duration-500",
        scrolled
          ? "border-b border-black/[0.07] bg-white/70 shadow-[0_8px_32px_-12px_rgba(13,27,42,0.18)] backdrop-blur-xl backdrop-saturate-150"
          : "border-b border-transparent bg-transparent backdrop-blur-none",
      )}
    >
      <nav
        aria-label="Primary"
        className="mx-auto flex h-14 max-w-[1400px] items-center justify-between gap-2 px-4 sm:h-[68px] sm:gap-4 sm:px-6 md:px-8"
      >
        <NavLink
          href={hrefFor("#hero")}
          aria-label="JRAT'S Studio, back to top"
          className="flex shrink-0 items-center"
        >
          <Logo className="w-[3.75rem] min-[400px]:w-[4.5rem] sm:w-[5.5rem]" />
        </NavLink>

        {/* Desktop links */}
        <ul onMouseLeave={() => setHovered(null)} className="hidden items-center gap-1 md:flex">
          {links.map((link) => (
            <li key={link.href} className="relative">
              <NavLink
                href={hrefFor(link.href)}
                onMouseEnter={() => setHovered(link.href)}
                onFocus={() => setHovered(link.href)}
                className="relative z-10 block rounded-full px-3 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-brand-ink/65 transition-colors duration-300 hover:text-brand-ink focus-visible:text-brand-ink lg:px-4"
              >
                {link.label}
              </NavLink>
              {hovered === link.href && (
                <motion.span
                  layoutId="nav-chip"
                  aria-hidden
                  className="absolute inset-0 rounded-full bg-black/[0.05] ring-1 ring-black/[0.06]"
                  transition={{ type: "spring", stiffness: 420, damping: 34 }}
                />
              )}
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2">
          <BookCall className={cn(buttonVariants({ size: "sm" }), "hidden rounded-full px-3 text-[12px] sm:inline-flex md:h-9 md:px-4 md:text-[13px]")}>
            Book a call
            <ArrowUpRight className="size-3 md:size-3.5" />
          </BookCall>
          <button
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-menu"
            onClick={() => setOpen((o) => !o)}
            className="flex size-9 items-center justify-center rounded-full text-brand-ink ring-1 ring-black/[0.1] transition-colors duration-300 hover:text-brand-accent focus:outline-none focus-visible:ring-brand-accent sm:size-10 md:hidden"
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={open ? "close" : "open"}
                initial={{ rotate: -45, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 45, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="flex"
              >
                {open ? <X className="size-5" /> : <Menu className="size-5" />}
              </motion.span>
            </AnimatePresence>
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="overflow-hidden border-t border-black/[0.07] bg-white md:hidden"
          >
            <ul className="divide-y divide-black/[0.07] px-4 sm:px-6">
              {links.map((link, i) => (
                <motion.li
                  key={link.href}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.04 + i * 0.04, duration: 0.25, ease: EASE }}
                >
                  <NavLink
                    href={hrefFor(link.href)}
                    onClick={() => setOpen(false)}
                    className="group flex items-center justify-between py-3.5 font-heading text-xl uppercase leading-none text-brand-ink transition-colors duration-300 hover:text-brand-accent"
                  >
                    {link.label}
                    <ArrowUpRight className="size-5 text-brand-ink/30 transition-colors duration-300 group-hover:text-brand-accent" />
                  </NavLink>
                </motion.li>
              ))}
            </ul>
            <div className="px-4 pb-4 pt-3 sm:px-6">
              <BookCall onActivate={() => setOpen(false)} className={cn(buttonVariants(), "w-full rounded-full")}>
                Book a 30-minute call
                <ArrowUpRight className="size-4" />
              </BookCall>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
